import {Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {ItemIdMap} from '@emm/core/models/item-id-mapping.model';
import {trim} from 'ramda';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {Observable} from 'rxjs';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {API_ERRORS} from "@emm/core/util/api-constants";

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-item-id-mappings-form',
    templateUrl: './item-id-mappings-form.component.html',
    styleUrls: ['./item-id-mappings-form.component.scss'],
})
export class ItemIdMappingsFormComponent implements OnInit {

    @Input() isSuperAdmin: boolean;
    @Input() isAdmin: boolean;
    canEdit: boolean;
    @Output() search = new EventEmitter<void>();
    @Output() upsert = new EventEmitter<void>();
    @Output() delete = new EventEmitter<void>();

    form: FormGroup;
    control: FormArray;
    searchForm: FormGroup;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    isReadOnly$ = this.authFacade.isReadOnly$;

    constructor(
        private authFacade: AuthFacadeService,
        private notificationService: NotificationService,
        private formBuilder: FormBuilder,
        private managementApiService: ManagementApiService
    ) {
    }


    ngOnInit(): void {
        this.canEdit = this.isSuperAdmin || this.isAdmin;
        this.searchForm = this.formBuilder.group({
            cfaMenuId: null,
            rmi: null,
            tag: null,
            pin: null
        });
        this.form = this.formBuilder.group({
            itemIdMappings: this.formBuilder.array([])
        });
        this.addRow();
        this.control = this.form.get('itemIdMappings') as FormArray;
    }

    getData(criteria: ItemIdMap): void {
        this.getAllItemIdMappingsBy(criteria)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                // tslint:disable-next-line:prefer-for-of
                for (let i = 0; i < response.length; i++) {
                    this.addRow(response[i]);
                }
                this.notificationService.success('Found ' + response.length + (response.length === 1 ? ' result' : ' results'));
            });
    }

    initFormGroupFromItemIdMapping(itemIdMapping?: ItemIdMap): FormGroup {
        const data = itemIdMapping ? itemIdMapping : {
            id: null,
            cfaMenuId: null,
            rmi: null,
            tag: null,
            pin: null
        };
        data['original'] = data;
        return this.formBuilder.group(data);
    }

    initItemIdMapFromFormGroup(formGroup: FormGroup): ItemIdMap {
        const data = {
            id: formGroup.controls.id.value,
            cfaMenuId: formGroup.controls.cfaMenuId.value,
            rmi: formGroup.controls.rmi.value,
            tag: this.trimToNull(formGroup.controls.tag.value), // some data scrubbing
            pin: formGroup.controls.pin.value
        };
        return data as ItemIdMap;
    }

    getItemIdMappingControls(): FormArray {
        return this.form.get('itemIdMappings') as FormArray;
    }

    addNewRowAtTop(): void {
        const control = this.form.get('itemIdMappings') as FormArray;
        control.insert(0, this.initFormGroupFromItemIdMapping());
    }

    addRow(itemIdMapping?: any): void {
        const control = this.form.get('itemIdMappings') as FormArray;
        control.push(this.initFormGroupFromItemIdMapping(itemIdMapping));
    }

    removeRow(rowIndex: any) {
        const formArray = this.form.get('itemIdMappings') as FormArray;
        formArray.removeAt(rowIndex);
    }

    resetRow(rowIndex: any) {
        const formArray = this.form.get('itemIdMappings') as FormArray;
        const formGroup = formArray.controls[rowIndex] as FormGroup;
        this.resetItemIdMapFormGroup(formGroup);
    }

    resetItemIdMapFormGroup(formGroup: FormGroup): void {
        formGroup.controls.cfaMenuId.reset(formGroup.controls.original.value.cfaMenuId);
        formGroup.controls.rmi.reset(formGroup.controls.original.value.rmi);
        formGroup.controls.tag.reset(formGroup.controls.original.value.tag);
        formGroup.controls.pin.reset(formGroup.controls.original.value.pin);
    }

    removeAllRows(): void {
        const control = this.form.get('itemIdMappings') as FormArray;
        control.clear();
    }

    handleSearch(): void {
        this.search.next();
    }

    doSearch(): void {
        const searchCriteria = {
            id: null,
            cfaMenuId: this.searchForm.controls.cfaMenuId.value,
            rmi: this.searchForm.controls.rmi.value,
            tag: this.trimToNull(this.searchForm.controls.tag.value),
            pin: this.searchForm.controls.pin.value,
        }
        this.removeAllRows();
        this.getData(searchCriteria)
    }

    resetSearch(): void {
        this.searchForm.controls.cfaMenuId.reset();
        this.searchForm.controls.rmi.reset();
        this.searchForm.controls.tag.reset();
        this.searchForm.controls.pin.reset();
    }

    handleSubmit(): void {
        this.upsert.next();
    }

    doSubmit(): void {
        const payload = this.buildPayloadForSave();
        if (this.validateWithMessages(payload)) {
            this.doSave(payload);
        }
    }

    buildPayloadForSave(): ItemIdMap[] {
        const formArray = this.form.get('itemIdMappings') as FormArray;
        const payload = [];
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < formArray.controls.length; i++) {
            const formGroup = formArray.controls[i] as FormGroup;
            if (this.hasNewData(formGroup)) {
                const data = this.initItemIdMapFromFormGroup(formGroup);
                payload.push(data);
            }
        }
        return payload;
    }

    hasNewData(formGroup: FormGroup): boolean {
        // not null data
        return (formGroup.controls.cfaMenuId.value != null
                || formGroup.controls.rmi.value != null
                || this.trimToNull(formGroup.controls.tag.value) != null // whitespaces don't count
                || formGroup.controls.pin.value != null)
            &&
            // and has new data
            (formGroup.controls.cfaMenuId.value !== formGroup.controls.original.value.cfaMenuId
                || formGroup.controls.rmi.value !== formGroup.controls.original.value.rmi
                || formGroup.controls.tag.value !== formGroup.controls.original.value.tag
                || formGroup.controls.pin.value !== formGroup.controls.original.value.pin);
    }

    // returns true if validations pass,
    // false otherwise (and gives a message)
    validateWithMessages(payload: ItemIdMap[]): boolean {
        // no data?
        if (payload.length === 0) {
            this.notificationService.error('Validation error (no data). Please enter data and try again.');
            return false;
        }
        // negative numbers?
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < payload.length; i++) {
            if (!this.isValidNullableNumber(payload[i].cfaMenuId)
                || !this.isValidNullableNumber(payload[i].rmi)
                || !this.isValidNullableNumber(payload[i].pin)) {
                this.notificationService.error('Validation error (negative number). Please fix and try again.');
                return false;
            }
        }
        // valid
        return true;
    }

    isValidNullableNumber(num: number): boolean {
        return num === null || num >= 0;
    }

    doSave(payload: ItemIdMap[]): void {
        this.saveAllItemIdMappings(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this.removeAllRows();
                    // tslint:disable-next-line:prefer-for-of
                    for (let i = 0; i < result.length; i++) {
                        this.addRow(result[i]);
                    }
                    this.notificationService.success('Successfully saved '
                        + result.length + (result.length === 1 ? ' record.' : ' records.'));
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

    doDelete(rowIndex: any): void {
        const formArray = this.form.get('itemIdMappings') as FormArray;
        const formGroup = formArray.controls[rowIndex] as FormGroup;
        const deletePayload = this.initItemIdMapFromFormGroup(formGroup);
        if (deletePayload.id !== null) {
            this.deleteItemIdMapping(deletePayload)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response) => {
                        formArray.removeAt(rowIndex);
                        this.notificationService.success('Delete succeeded.');
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                });
        } else {
            this.notificationService.error('Validation error (no ID for row). Could not perform delete.');
        }
    }

    trimToNull(val: string): string {
        return val === null ? null :
            ('' === trim(val) ? null : trim(val));
    }

    getAllItemIdMappingsBy(criteria: ItemIdMap): Observable<ItemIdMap[]> {
        return this.managementApiService.getAllItemIdMappingsBy(criteria);
    }

    saveAllItemIdMappings(itemIdMappings: any): any {
        return this.managementApiService.saveAllItemIdMappings(itemIdMappings);
    }

    deleteItemIdMapping(itemIdMapping: ItemIdMap): any {
        return this.managementApiService.deleteItemIdMapping(itemIdMapping);
    }
}
